import { Component, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { ErrorHandler } from "@/lib/errors/ErrorHandler";
import { AppError } from "@/lib/errors/ErrorTypes";
import { logger } from "@/lib/utils/logger";

interface Props {
  children: ReactNode;
  fallback?: (error: AppError, resetError: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  error: AppError | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Normalize error to AppError
    const appError = ErrorHandler.normalizeError(error);
    return { hasError: true, error: appError };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const appError = ErrorHandler.normalizeError(error);
    const handled = ErrorHandler.handle(appError, {
      operation: "ErrorBoundary",
      timestamp: new Date(),
    });

    posthog.capture('client_error', {
      error: error.message,
      component: errorInfo.componentStack,
      page: window.location.pathname,
    })

    // Report to Sentry with component stack context
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack ?? undefined } },
    });

    logger.error("ErrorBoundary caught an error:", {
      error: handled.error,
      errorInfo,
      canRetry: handled.canRetry,
      recoveryStrategy: handled.recoveryStrategy,
    });

    this.setState({ error: appError, errorInfo });
  }

  resetError = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.resetError);
      }

      // Default error UI
      const handled = ErrorHandler.handle(this.state.error, {
        operation: "ErrorBoundary",
        timestamp: new Date(),
      });

      return (
        <div style={{ padding: "20px", fontFamily: "system-ui, sans-serif" }}>
          <h1 style={{ color: "#dc2626", marginBottom: "16px" }}>
            Something went wrong
          </h1>
          <p style={{ color: "#6b7280", marginBottom: "16px" }}>
            {handled.userMessage}
          </p>
          <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
            <button
              onClick={this.resetError}
              style={{
                padding: "8px 16px",
                backgroundColor: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "8px 16px",
                backgroundColor: "#6b7280",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Reload Page
            </button>
          </div>
          {import.meta.env.DEV && this.state.errorInfo && (
            <details style={{ marginTop: "16px" }}>
              <summary style={{ cursor: "pointer", color: "#6b7280" }}>
                Error Details (Dev Only)
              </summary>
              <pre
                style={{
                  background: "#f5f5f5",
                  padding: "10px",
                  borderRadius: "4px",
                  marginTop: "8px",
                  fontSize: "12px",
                  overflow: "auto",
                }}
              >
                {this.state.error.toString()}
                {this.state.error.stack}
                {"\n\n"}
                {this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}


